"use client";

import React from "react";
import {
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import "./EarningsSnapshot.css";

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

const currencySymbols = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  CHF: "CHF ",
  CAD: "C$",
  AUD: "A$",
};

const formatValue = (value, unit, currency) => {
  if (value === null || value === undefined || value === "") return "n/a";
  if (typeof value !== "number") return String(value);

  const formatted = numberFormatter.format(value);
  const symbol = currencySymbols[currency] || "";
  if (unit === "millions") return `${symbol}${formatted}M`;
  if (unit === "billions") return `${symbol}${formatted}B`;
  if (unit === "per_share") return `${symbol || "$"}${formatted}`;
  if (unit === "USD_millions") return `$${formatted}M`;
  if (unit === "USD_billions") return `$${formatted}B`;
  if (unit === "USD") return `$${formatted}`;
  if (unit === "percent") return `${formatted}%`;
  return formatted;
};

const getStatusColor = (status) => {
  if (status === "beat" || status === "raised" || status === "positive") {
    return "#15803d";
  }
  if (status === "miss" || status === "lowered" || status === "negative") {
    return "#b91c1c";
  }
  return "#64748b";
};

const SourceLink = ({ url }) =>
  url ? (
    <a
      className="er-source-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open source"
      aria-label="Open source"
    >
      <ExternalLink size={13} aria-hidden="true" />
    </a>
  ) : null;

const DumbbellMetric = ({ item }) => {
  const actual = Number(item.actual);
  const estimate = Number(item.estimate);
  const hasPair = Number.isFinite(actual) && Number.isFinite(estimate);
  const low = hasPair ? Math.min(actual, estimate) : 0;
  const high = hasPair ? Math.max(actual, estimate) : 1;
  const spread = high - low || Math.max(Math.abs(high) * 0.08, 1);
  const domainMin = low - spread * 0.6;
  const domainMax = high + spread * 0.6;
  const x = (value) =>
    24 + ((value - domainMin) / (domainMax - domainMin)) * 272;
  const statusColor = getStatusColor(item.status);

  return (
    <div className="er-metric-row">
      <div className="er-metric-heading">
        <div>
          <span className="er-metric-name">{item.metric}</span>
          {item.basis && item.basis !== "unknown" && (
            <span className="er-basis">{item.basis}</span>
          )}
        </div>
        <div className="er-surprise" style={{ color: statusColor }}>
          {item.surprisePercent !== null &&
          item.surprisePercent !== undefined &&
          Number.isFinite(Number(item.surprisePercent))
            ? `${Number(item.surprisePercent) > 0 ? "+" : ""}${numberFormatter.format(
                Number(item.surprisePercent),
              )}%`
            : item.status === "in_line"
              ? "in line"
              : item.status}
          <SourceLink url={item.sourceUrl} />
        </div>
      </div>

      {hasPair ? (
        <svg
          className="er-dumbbell"
          viewBox="0 0 320 48"
          role="img"
          aria-label={`${item.metric}: estimate ${formatValue(
            estimate,
            item.unit,
            item.currency,
          )}, actual ${formatValue(actual, item.unit, item.currency)}`}
        >
          <line
            x1={x(estimate)}
            y1="19"
            x2={x(actual)}
            y2="19"
            stroke="#94a3b8"
            strokeWidth="3"
          />
          <circle
            cx={x(estimate)}
            cy="19"
            r="6"
            fill="#fff"
            stroke="#64748b"
            strokeWidth="2"
          />
          <circle
            cx={x(actual)}
            cy="19"
            r="7"
            fill={statusColor}
            stroke="#fff"
            strokeWidth="2"
          />
          <text
            x={x(estimate)}
            y="43"
            textAnchor="middle"
            className="er-chart-label"
          >
            E {formatValue(estimate, item.unit, item.currency)}
          </text>
          <text
            x={x(actual)}
            y="10"
            textAnchor="middle"
            className="er-chart-label er-chart-label-actual"
          >
            A {formatValue(actual, item.unit, item.currency)}
          </text>
        </svg>
      ) : (
        <div className="er-single-value">
          {formatValue(item.actual, item.unit, item.currency)}
        </div>
      )}
    </div>
  );
};

const TrendMetric = ({ trend }) => {
  const points = (trend.points || []).filter((point) =>
    Number.isFinite(Number(point.value)),
  );
  if (points.length < 2) return null;

  const width = 300;
  const height = 74;
  const values = points.map((point) => Number(point.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || Math.max(Math.abs(max) * 0.08, 1);
  const coords = points.map((point, index) => ({
    x: 10 + (index / (points.length - 1)) * (width - 20),
    y: 10 + ((max - Number(point.value)) / range) * (height - 30),
    ...point,
  }));
  const path = coords
    .map((point, index) => `${index ? "L" : "M"}${point.x},${point.y}`)
    .join(" ");
  const color =
    trend.direction === "down"
      ? "#b91c1c"
      : trend.direction === "up"
        ? "#15803d"
        : "#2563eb";

  return (
    <div className="er-trend">
      <div className="er-trend-heading">
        <span>{trend.metric}</span>
        <span className="er-trend-direction" style={{ color }}>
          {trend.direction === "up" ? (
            <TrendingUp size={15} />
          ) : trend.direction === "down" ? (
            <TrendingDown size={15} />
          ) : null}
          {formatValue(values[values.length - 1], trend.unit, trend.currency)}
          <SourceLink url={trend.sourceUrl} />
        </span>
      </div>
      <svg
        className="er-sparkline"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${trend.metric} trend`}
      >
        <path d={path} fill="none" stroke={color} strokeWidth="2.5" />
        {coords.map((point) => (
          <circle
            key={`${point.period}-${point.x}`}
            cx={point.x}
            cy={point.y}
            r="3.5"
            fill="#fff"
            stroke={color}
            strokeWidth="2"
          />
        ))}
        <text x="10" y={height - 2} className="er-chart-label">
          {points[0].period}
        </text>
        <text
          x={width - 10}
          y={height - 2}
          textAnchor="end"
          className="er-chart-label"
        >
          {points[points.length - 1].period}
        </text>
      </svg>
    </div>
  );
};

const EarningsSnapshot = ({ data }) => {
  if (!data || !data.hasEarningsData) return null;

  const metrics = data.actualVsEstimate || [];
  const trends = (data.trends || []).filter(
    (trend) => (trend.points || []).length >= 2,
  );
  const guidance = data.guidance || [];
  const highlights = data.highlights || [];

  return (
    <section
      className="er-snapshot"
      aria-label={`${data.symbol || "Company"} earnings snapshot`}
    >
      <div className="er-snapshot-header">
        <div>
          <div className="er-eyebrow">Earnings snapshot</div>
          <div className="er-title-line">
            <strong>{data.quarter || "Latest report"}</strong>
            {data.reportDate && <span>{data.reportDate}</span>}
          </div>
        </div>
        {data.needsVerification && (
          <span
            className="er-verification"
            title={(data.verificationNotes || []).join(" ")}
          >
            <AlertTriangle size={14} aria-hidden="true" /> Verify
          </span>
        )}
      </div>

      {data.headline && <p className="er-headline">{data.headline}</p>}

      {highlights.length > 0 && (
        <div className="er-highlights">
          {highlights.slice(0, 4).map((item, index) => (
            <div className="er-highlight" key={`${item.label}-${index}`}>
              <span>{item.label}</span>
              <strong style={{ color: getStatusColor(item.sentiment) }}>
                {item.value}
              </strong>
            </div>
          ))}
        </div>
      )}

      {metrics.length > 0 && (
        <div className="er-section">
          <h4>Actual vs consensus</h4>
          <div className="er-metric-grid">
            {metrics.map((item, index) => (
              <DumbbellMetric item={item} key={`${item.metric}-${index}`} />
            ))}
          </div>
        </div>
      )}

      {trends.length > 0 && (
        <div className="er-section">
          <h4>Trajectory</h4>
          <div className="er-trend-grid">
            {trends.map((trend, index) => (
              <TrendMetric trend={trend} key={`${trend.metric}-${index}`} />
            ))}
          </div>
        </div>
      )}

      {guidance.length > 0 && (
        <div className="er-section">
          <h4>Guidance</h4>
          <div className="er-guidance-list">
            {guidance.map((item, index) => (
              <div className="er-guidance-row" key={`${item.metric}-${index}`}>
                <span className="er-guidance-name">{item.metric}</span>
                <span className="er-guidance-values">
                  <span>{item.previous || "Previous n/a"}</span>
                  <ArrowRight size={14} aria-hidden="true" />
                  <strong style={{ color: getStatusColor(item.status) }}>
                    {item.current}
                  </strong>
                  <SourceLink url={item.sourceUrl} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default EarningsSnapshot;
